import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Clock, FileSignature, MapPin } from 'lucide-react';
import SEO from '../components/SEO';
import TrustSubNav from '../components/TrustSubNav';
import { CompaniesOfficeBlock, CompaniesOfficeStrip } from '../components/CompaniesOfficeBlock';

// =============================================================================
// GIWAHS — Trust Centre · Compliance page (review-only)
// Source of truth: /app/memory/founder_vault/40_GIWAHS_Compliance_Page.md
// =============================================================================

const ALIGNED = [
  { fw: 'EU GDPR (Regulation (EU) 2016/679)',                  ref: 'Privacy Policy · DPA · Security Policy' },
  { fw: 'UK GDPR + Data Protection Act 2018',                  ref: 'Privacy Policy · DPA · Security Policy' },
  { fw: 'Swiss FADP (revised 2023)',                            ref: 'Data Processing Agreement' },
  { fw: 'NZ Privacy Act 2020',                                 ref: 'Privacy Policy · Security Policy' },
  { fw: 'Australian Privacy Act 1988 + APPs',                  ref: 'Privacy Policy · Security Policy' },
  { fw: 'California CCPA / CPRA',                              ref: 'Privacy Policy' },
  { fw: 'Colorado · Virginia · Connecticut · Utah · Texas',    ref: 'Privacy Policy' },
  { fw: 'ePrivacy Directive + PECR (UK)',                      ref: 'Cookie Policy' },
  { fw: 'PCI-DSS (via Stripe outsourcing — SAQ-A scope)',     ref: 'Security Policy' },
  { fw: 'CAN-SPAM · CASL · NZ UEMA · AU SPAM Act',             ref: 'Privacy Policy' },
];

const ROADMAP = [
  { fw: 'SOC 2 Type I',                       target: 'Year 1–2',  status: 'Planning — scope being defined' },
  { fw: 'SOC 2 Type II',                      target: 'Year 2',    status: 'Following Type I' },
  { fw: 'ISO/IEC 27001',                      target: 'Year 2–3',  status: 'Gap analysis after SOC 2 Type I' },
  { fw: 'FedRAMP',                            target: 'Not in scope', status: '—' },
];

const RESIDENCY = [
  { data: 'Application data (DB, auth, storage)', primary: 'Supabase / AWS (region configurable)', mech_eu: 'EU SCCs · UK IDTA', mech_apac: 'NZ IPP 12 · APP 8' },
  { data: 'Payment metadata',                     primary: 'Stripe (USA primary)',                  mech_eu: 'EU SCCs · UK IDTA · PCI-DSS L1', mech_apac: 'Contractual safeguards' },
  { data: 'Email content + delivery metadata',    primary: 'Resend (USA primary)',                   mech_eu: 'EU SCCs · UK IDTA', mech_apac: 'Contractual safeguards' },
  { data: 'Edge / network logs',                   primary: 'Cloudflare (global POPs)',              mech_eu: 'EU SCCs · UK IDTA', mech_apac: 'Contractual safeguards' },
  { data: 'Corporate email & files',              primary: 'Google Workspace (USA primary)',         mech_eu: 'Google DPA + EU SCCs', mech_apac: 'Contractual safeguards' },
  { data: 'LLM API (gated, currently disabled)',  primary: 'OpenAI (USA primary)',                   mech_eu: 'OpenAI DPA + EU SCCs', mech_apac: 'Contractual safeguards' },
];

const TrustCompliance = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Trust Centre', url: '/trust' },
    { name: 'Compliance', url: '/trust/compliance' },
  ];

  return (
    <div data-testid="trust-compliance-page" className="bg-white">
      <SEO
        title="Compliance — Trust Centre"
        description="GIWAHS compliance posture: EU GDPR, UK GDPR, Swiss FADP, NZ Privacy Act 2020, Australia Privacy Act, CCPA/CPRA + US state laws, PCI-DSS via Stripe, ePrivacy. SOC 2 + ISO 27001 roadmap, data residency table, pre-signed DPA."
        breadcrumbs={breadcrumbs}
      />

      {/* Hero */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <Link to="/trust" className="text-xs uppercase tracking-wider text-neutral-500 font-mono hover:text-neutral-900">
            ← Trust Centre
          </Link>
          <span className="mt-6 block label-overline">/ Compliance</span>
          <h1
            className="mt-4 max-w-4xl font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}
          >
            What we comply with today,<br />
            and what we're working toward.<br />
            <span className="text-neutral-500">In writing.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
            Enterprise partners ask three questions: <strong>what do we comply with, why does it
            matter, and how can it be verified?</strong> This page answers all three — the current
            compliance posture of <strong>Whaitiri Black Limited</strong> (trading as GIWAHS),
            dated, in writing, and backed by the documents in the Trust Centre library.
          </p>
          <a
            data-testid="compliance-dpa-download"
            href="/legal/dpa.html"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 border-b border-neutral-900 pb-1 text-sm font-medium hover:gap-3 transition-all"
          >
            <FileSignature size={14} strokeWidth={1.5} /> Open the pre-signed DPA <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      <TrustSubNav />

      {/* Status board */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ 01 — Status board</span>
          <div className="mt-6 grid gap-px bg-black md:grid-cols-3">
            {[
              { l: 'Aligned frameworks',   v: '10 in force today — detailed below' },
              { l: 'DPA status',           v: 'Published v1.0 — pre-signed by GIWAHS' },
              { l: 'Standardised questionnaires', v: 'Supported on request — see §06' },
            ].map((s) => (
              <div key={s.l} className="bg-white p-6">
                <div className="label-overline tracking-wider">{s.l}</div>
                <div className="mt-3 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aligned frameworks */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 02 — Frameworks we comply with</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Aligned today.
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse border border-black bg-white text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="border border-black p-4 text-left label-overline">Framework</th>
                  <th className="border border-black p-4 text-left label-overline">Status</th>
                  <th className="border border-black p-4 text-left label-overline">Reference</th>
                </tr>
              </thead>
              <tbody>
                {ALIGNED.map((r) => (
                  <tr key={r.fw}>
                    <td className="border border-black p-4 font-medium">{r.fw}</td>
                    <td className="border border-black p-4 text-neutral-700">
                      <span className="inline-flex items-center gap-1.5"><Check size={14} strokeWidth={2} /> Aligned</span>
                    </td>
                    <td className="border border-black p-4 text-neutral-500 font-mono text-xs">{r.ref}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 03 — Working toward</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Honest roadmap, with dates.
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse border border-black bg-white text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="border border-black p-4 text-left label-overline">Framework</th>
                  <th className="border border-black p-4 text-left label-overline">Target</th>
                  <th className="border border-black p-4 text-left label-overline">Status</th>
                </tr>
              </thead>
              <tbody>
                {ROADMAP.map((r) => (
                  <tr key={r.fw}>
                    <td className="border border-black p-4 font-medium">{r.fw}</td>
                    <td className="border border-black p-4 text-neutral-700">
                      <span className="inline-flex items-center gap-1.5"><Clock size={14} strokeWidth={1.5} /> {r.target}</span>
                    </td>
                    <td className="border border-black p-4 text-neutral-700">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Data residency */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 04 — Data residency & transfer mechanisms</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Where your data lives, and how it travels.
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table data-testid="compliance-residency-table" className="w-full min-w-[860px] border-collapse border border-black bg-white text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="border border-black p-4 text-left label-overline">Data category</th>
                  <th className="border border-black p-4 text-left label-overline">Primary residency</th>
                  <th className="border border-black p-4 text-left label-overline">Mechanism (EU/UK/CH origin)</th>
                  <th className="border border-black p-4 text-left label-overline">Mechanism (NZ/AU origin)</th>
                </tr>
              </thead>
              <tbody>
                {RESIDENCY.map((r) => (
                  <tr key={r.data}>
                    <td className="border border-black p-4 font-medium">{r.data}</td>
                    <td className="border border-black p-4 text-neutral-700">
                      <span className="inline-flex items-center gap-1.5"><MapPin size={14} strokeWidth={1.5} /> {r.primary}</span>
                    </td>
                    <td className="border border-black p-4 text-neutral-700">{r.mech_eu}</td>
                    <td className="border border-black p-4 text-neutral-700">{r.mech_apac}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-neutral-500 font-mono">
            Full{' '}
            <a href="/legal/subprocessors.html" target="_blank" rel="noopener noreferrer" className="underline">
              Subprocessor Register
            </a>{' '}
            — available in the Trust Centre document library.
          </p>
        </div>
      </section>

      {/* DPA paths + questionnaire SLA */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-2 md:px-8 md:py-24">
          <div>
            <span className="label-overline">/ 05 — Data Processing Addendum (DPA)</span>
            <h3 className="mt-3 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>Three paths.</h3>
            <ol className="mt-6 space-y-3 text-sm leading-relaxed text-neutral-700">
              <li><strong>1. Self-serve.</strong> Open the <a href="/legal/dpa.html" target="_blank" rel="noopener noreferrer" className="underline">Data Processing Agreement</a> from the document library. Customer countersigns by reply email.</li>
              <li><strong>2. Email-back countersignature.</strong> Send your signed copy to legal@giwahs.com; we counter-sign and return within 2 business days.</li>
              <li><strong>3. Custom DPA.</strong> Enterprise tier — we accept reasonable amendments. Material changes extend turnaround to 5–7 business days.</li>
            </ol>
            <a
              data-testid="compliance-dpa-email"
              href="mailto:legal@giwahs.com?subject=DPA%20Request"
              className="mt-6 inline-flex items-center gap-2 border-b border-neutral-900 pb-1 text-sm font-medium hover:gap-3 transition-all"
            >
              Email legal@giwahs.com <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
          </div>
          <div>
            <span className="label-overline">/ 06 — Enterprise procurement support</span>
            <h3 className="mt-3 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>SLAs you can plan around.</h3>
            <ul className="mt-6 space-y-3 text-sm leading-relaxed text-neutral-700">
              <li><strong>Acknowledgement:</strong> within 2 business days.</li>
              <li><strong>250-Q standard questionnaire:</strong> 5 business days (Enterprise) · 10 business days (Gold / Elite).</li>
              <li><strong>NDA:</strong> mutual same-day countersignature; ours available on request.</li>
            </ul>
            <a
              data-testid="compliance-contact-compliance"
              href="mailto:security@giwahs.com?subject=Compliance%20Questionnaire"
              className="mt-6 inline-flex items-center gap-2 border-b border-neutral-900 pb-1 text-sm font-medium hover:gap-3 transition-all"
            >
              Email security@giwahs.com <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-6 px-5 py-12 md:grid-cols-3 md:px-8">
          {[
            { label: 'Compliance & questionnaires', email: 'security@giwahs.com', t: 'compliance-contact-compliance' },
            { label: 'Privacy & DPA',                email: 'privacy@giwahs.com',  t: 'compliance-contact-privacy' },
            { label: 'Legal',                        email: 'legal@giwahs.com',    t: 'compliance-contact-legal' },
          ].map((c) => (
            <div key={c.email} data-testid={c.t} className="border border-neutral-900 bg-white p-5">
              <div className="label-overline tracking-wider">{c.label}</div>
              <a href={`mailto:${c.email}`} className="mt-3 block text-sm underline">{c.email}</a>
            </div>
          ))}
        </div>
      </section>
      {/* Companies Office verification (D-057) */}
      <CompaniesOfficeBlock />
      <CompaniesOfficeStrip />
    </div>
  );
};

export default TrustCompliance;
